/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useCallback, useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { ChatOnboardingGreetings } from '@atlassian/conversation-assistant-ui-components/ui/ChatOnboardingGreetings';
import {
	ChatOnboardingPrompts,
	type UserPersonaType,
} from '@atlassian/conversation-assistant-ui-components/ui/ChatOnboardingPrompts';

import { recordOnboardingCompletedForHoldout } from '../../controllers/chat-onboarding-holdout/recordOnboardingCompletedForHoldout';

const styles = cssMap({
	container: {
		width: '100%',
		height: '100%',
		flexGrow: 1,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-start',
	},
});

const OnboardingStep = {
	greetings: 'greetings',
	prompts: 'prompts',
} as const;

const onboardingSubScreenByStep: Record<keyof typeof OnboardingStep, string> = {
	[OnboardingStep.greetings]: 'onboarding-greetings',
	[OnboardingStep.prompts]: 'onboarding-prompts',
};

export type AllowedProductsType = 'jira' | 'confluence';

const ChatOnboardingImpl = ({ product }: { product: AllowedProductsType }) => {
	const [onboardingStep, setOnboardingStep] = useState<keyof typeof OnboardingStep>(
		OnboardingStep.greetings,
	);
	const publish = usePublish('ai-mate');
	const { sendAnalyticsEvent } = useAnalytics();
	const userPersona: UserPersonaType = 'consumer'; // TODO: add user persona from the store

	const handleOnboardingComplete = useCallback(
		(promptMessage: string) => {
			publish({
				type: 'chat-new',
				source: 'ai-mate-onboarding',
				openChat: false, // If users is seeing Onboarding, Chat is already open
				data: {
					name: promptMessage,
					prompt: promptMessage,
					sourceId: 'ai-mate-onboarding',
					dialogues: [],
				},
			});

			recordOnboardingCompletedForHoldout();
		},
		[publish],
	);

	const handleOnboardingStepChange = useCallback(
		(buttonName: string, additionalAttributes: Record<string, string> = {}) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'chatOnboardingButton',
				attributes: {
					mode: 'mini-modal',
					buttonName,
					persona: userPersona,
					step: onboardingStep,
					...additionalAttributes,
				},
			});

			switch (onboardingStep) {
				case OnboardingStep.prompts:
					handleOnboardingComplete(additionalAttributes.promptMessage);
					break;
				case OnboardingStep.greetings:
					setOnboardingStep(OnboardingStep.prompts);
					break;
				default:
					// always show greetings step on first visit
					setOnboardingStep(OnboardingStep.greetings);
			}
		},
		[sendAnalyticsEvent, userPersona, onboardingStep, handleOnboardingComplete],
	);

	const handleStepViewed = useCallback(
		(additionalAttributes: Record<string, string> = {}) => {
			sendAnalyticsEvent({
				eventType: 'screen',
				name: 'chatOnboardingScreen',
				attributes: {
					mode: 'mini-modal',
					persona: userPersona,
					step: onboardingStep,
					...additionalAttributes,
				},
			});
		},
		[sendAnalyticsEvent, userPersona, onboardingStep],
	);

	return (
		<Box xcss={styles.container}>
			<UFOCustomData
				data={{
					conversationHistorySubScreen: onboardingSubScreenByStep[onboardingStep],
					conversationMessagesResponseCount: 0,
				}}
			/>
			{onboardingStep === OnboardingStep.greetings && (
				<ChatOnboardingGreetings
					onNext={handleOnboardingStepChange}
					onStepViewed={handleStepViewed}
					product={product}
				/>
			)}
			{onboardingStep === OnboardingStep.prompts && (
				<ChatOnboardingPrompts
					userPersona={userPersona}
					onNext={handleOnboardingStepChange}
					onStepViewed={handleStepViewed}
					product={product}
				/>
			)}
		</Box>
	);
};

export const ChatOnboarding = ({ product }: { product: string }): JSX.Element | null => {
	if (
		(product === 'confluence' && fg('rovo_onboarding_modal_confluence_chat_fg')) ||
		(product === 'jira' && fg('rovo_onboarding_modal_jira_chat_filter_fg'))
	) {
		return <ChatOnboardingImpl product={product as AllowedProductsType} />;
	}
	return null;
};
